import type { Ref } from 'vue';
import type { SaveDraftMode } from '@/components/dialogs/SaveJobDraftDialog.vue';
import type { AfterBook } from '@/composables/jobBookings/useJobBookingSubmit';
import type { JobBookingProductsComposable } from '@/composables/views/useJobBookingProducts';
import type * as openapi from '@/helpers/api/types/openapiOverrides';
import type {
  BookingProduct,
  BookingStop,
  JobBookingCustomer,
  StopNumber,
} from '@/models/advancedJobBooking';

import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import useJobBookingCustomerData, {
  type RefConfigTuple,
  createBookingStop,
  nextBusinessDayISO,
} from '@/composables/data/useJobBookingCustomerData';
import useJobBookingSaveDraft from '@/composables/jobBookings/useJobBookingSaveDraft';
import useJobBookingSubmit from '@/composables/jobBookings/useJobBookingSubmit';
import useJobBookingProducts from '@/composables/views/useJobBookingProducts';
import {
  CLONE_JOB_DATE_QUERY,
  CLONE_JOB_ID_QUERY,
  buildBookApiBody,
  buildJobBookingItem,
  mapDetailedJobToBooking,
  parseJobBookingDraft,
} from '@/helpers/advancedJobBooking';
import {
  fetchDetailedJob,
  fetchGetJobDraftById,
  fetchPostJobBookApi,
} from '@/helpers/fetchers';
import { showErrorNotification } from '@/helpers/notifiers';
import { reclampDraftStops } from '@/helpers/stopsTimeline';
import * as routeNames from '@/constants/routeNames';
import { createEmptyCustomer } from '@/models/advancedJobBooking';

export interface StopPair {
  fromStop: StopNumber;
  toStop: StopNumber;
}

export interface AdvancedJobBookingViewComposable extends JobBookingProductsComposable {
  customer: Ref<JobBookingCustomer>;
  stops: Ref<BookingStop[]>;
  stopPairs: Ref<StopPair[]>;
  attachments: Ref<File[]>;
  refConfigs: Ref<RefConfigTuple>;
  addressItems: Ref<openapi.AddressGETOpenapi[]>;
  addressLoading: Ref<boolean>;
  isSubmitting: Ref<boolean>;
  isLoadingDraft: Ref<boolean>;
  bookingErrors: Ref<string[]>;
  draftId: Ref<number | null>;
  draftName: Ref<string>;
  submit: (after?: AfterBook) => Promise<boolean>;
  saveDraft: (name: string, mode?: SaveDraftMode) => Promise<void>;
  loadDraft: (id: number) => Promise<void>;
  loadClone: (jobId: number, jobDate?: string) => Promise<void>;
  goToDraftsList: () => void;
  reset: () => void;
}

export default function useAdvancedJobBookingView(): AdvancedJobBookingViewComposable {
  const router = useRouter();
  const route = useRoute();

  const { customer, refConfigs, addressItems, addressLoading } =
    useJobBookingCustomerData();

  const stops = ref<BookingStop[]>([
    createBookingStop(1),
    createBookingStop(2),
  ]);

  const productsApi = useJobBookingProducts();

  const stopPairs = computed<StopPair[]>(() =>
    stops.value.slice(0, -1).map((stop, index) => ({
      fromStop: stop.number,
      toStop: stops.value[index + 1].number,
    })),
  );

  function seedMissingProducts(): void {
    stopPairs.value.forEach(({ fromStop, toStop }) => {
      if (productsApi.productsForPair(fromStop, toStop).length === 0) {
        productsApi.addProduct(fromStop, toStop);
      }
    });
  }
  seedMissingProducts();

  watch(
    () => stops.value.length,
    () => {
      productsApi.removeProductsForRemovedStops(stops.value.length);
      seedMissingProducts();
    },
  );

  const attachments = ref<File[]>([]);

  const isSubmitting = ref(false);
  // Per-job `error_reason`s from the last failed booking, shown in the error panel.
  const bookingErrors = ref<string[]>([]);
  const isLoadingDraft = ref(false);
  // Id/name of the draft currently loaded (if any) — lets "Save as draft" update it in place.
  const draftId = ref<number | null>(null);
  const draftName = ref('');
  // Shared by loadDraft/loadClone so a superseding load aborts the previous one.
  let loadController: AbortController | undefined;

  function reset(): void {
    customer.value = createEmptyCustomer();
    stops.value = [createBookingStop(1), createBookingStop(2)];
    attachments.value = [];
    bookingErrors.value = [];
    draftId.value = null;
    draftName.value = '';
    productsApi.reset();
    seedMissingProducts();
  }

  const { submit } = useJobBookingSubmit({
    stops,
    attachments,
    isSubmitting,
    bookingErrors,
    reset,
    router,
    postBooking: (signal) =>
      fetchPostJobBookApi(
        buildBookApiBody(
          customer.value,
          stops.value,
          productsApi.products.value,
        ),
        { signal },
      ),
  });

  const { saveDraft } = useJobBookingSaveDraft({
    draftId,
    draftName,
    buildContent: () =>
      buildJobBookingItem(
        customer.value,
        stops.value,
        productsApi.products.value,
      ),
  });

  // Applies a draft or cloned job to the form, re-clamping stop windows to the next valid booking day.
  // `draft` is omitted for a clone, clearing draftId/draftName so "Save as draft" starts a new one.
  function applyParsedBooking(
    parsed: {
      customer: JobBookingCustomer;
      stops: BookingStop[];
      products: BookingProduct[];
    },
    draft?: { id: number; name: string },
  ): void {
    const minDate = nextBusinessDayISO();
    customer.value = parsed.customer;
    stops.value = reclampDraftStops(parsed.stops, minDate);
    productsApi.setProducts(parsed.products);
    bookingErrors.value = [];
    draftId.value = draft?.id ?? null;
    draftName.value = draft?.name ?? '';
  }

  async function loadDraft(id: number): Promise<void> {
    if (loadController) loadController.abort();
    loadController = new AbortController();
    isLoadingDraft.value = true;
    try {
      const draft = await fetchGetJobDraftById(id, {
        signal: loadController.signal,
      });
      const parsed = parseJobBookingDraft(draft.content);
      if (!parsed) {
        showErrorNotification({ title: 'Draft format not supported' });
        return;
      }
      applyParsedBooking(parsed, { id, name: draft.name });
    } catch {
      // Fetch errors are notified inside the fetcher
    } finally {
      isLoadingDraft.value = false;
    }
  }

  async function loadClone(jobId: number, jobDate?: string): Promise<void> {
    if (loadController) loadController.abort();
    loadController = new AbortController();
    isLoadingDraft.value = true;
    try {
      const job = await fetchDetailedJob(
        { jobId, jobDate },
        { signal: loadController.signal },
      );
      applyParsedBooking(mapDetailedJobToBooking(job));
    } catch {
      // Fetch errors are notified inside the fetcher
    } finally {
      isLoadingDraft.value = false;
    }
  }

  watch(
    () => route.params.id,
    (id) => {
      const routeDraftId = Number(id);
      if (id && !Number.isNaN(routeDraftId)) void loadDraft(routeDraftId);
    },
    { immediate: true },
  );

  // Clone flow: the "Clone" dialog navigates here with the source job in the query.
  watch(
    () => route.query[CLONE_JOB_ID_QUERY],
    (rawId) => {
      const jobId = Number(rawId);
      if (!rawId || Number.isNaN(jobId)) return;
      const jobDate =
        typeof route.query[CLONE_JOB_DATE_QUERY] === 'string'
          ? route.query[CLONE_JOB_DATE_QUERY]
          : undefined;
      void loadClone(jobId, jobDate);
    },
    { immediate: true },
  );

  function goToDraftsList(): void {
    void router.push({ name: routeNames.DRAFT_JOB_BOOKING });
  }

  return {
    ...productsApi,
    customer,
    stops,
    stopPairs,
    attachments,
    refConfigs,
    addressItems,
    addressLoading,
    isSubmitting,
    isLoadingDraft,
    bookingErrors,
    draftId,
    draftName,
    submit,
    saveDraft,
    loadDraft,
    loadClone,
    goToDraftsList,
    reset,
  };
}
